/**
 * The «Полная транскрибация» tab (CR-35): every `transcript`-purpose file row of the opened
 * recording (one per channel since per-channel transcripts) is fetched, parsed and merged
 * into ONE chronological feed sorted by segment start (stable tie-break by channel order =
 * transcript file order). Each row reads `[m:ss] Name: text`; names resolve through one
 * directory by-ids lookup over the rows' channel identities (`kc-<uuid>`, screen shares
 * `kc-<uuid>-screen` get the «Экран: » prefix), falling back to «Канал N» when the identity
 * is unknown or the directory answers 503/502/network. A channel whose transcript fails to
 * fetch or parse degrades to a muted per-channel note while the rest still renders. The feed
 * is cached per opened recording (the parent remounts per recording, so the cache dies with
 * it) and is not refetched on tab switches.
 */

import { LoaderCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchUsersByIds } from '../api/directory';
import type { DirectoryUser } from '../api/directory';
import { fetchRecordingFile } from '../api/recording';
import type { RecordingFile } from '../api/recording';
import type { RoomRecording } from './api';

export interface TranscriptSegment {
  readonly start: number;
  readonly end: number;
  readonly text: string;
}

export interface TranscriptDoc {
  readonly language: string | null;
  readonly segments: readonly TranscriptSegment[];
}

/** Narrows the canonical transcript JSON (worker-written .transcript.json); null on garbage. */
export function parseTranscript(value: unknown): TranscriptDoc | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (!Array.isArray(record.segments)) {
    return null;
  }
  const segments: TranscriptSegment[] = [];
  for (const entry of record.segments) {
    if (typeof entry !== 'object' || entry === null) {
      return null;
    }
    const seg = entry as Record<string, unknown>;
    const start = typeof seg.start === 'number' && Number.isFinite(seg.start) ? seg.start : null;
    const end = typeof seg.end === 'number' && Number.isFinite(seg.end) ? seg.end : null;
    const text = typeof seg.text === 'string' ? seg.text.trim() : null;
    if (start === null || end === null || text === null || text.length === 0) {
      return null;
    }
    segments.push({ start, end, text });
  }
  const language = typeof record.language === 'string' ? record.language : null;
  return { language, segments };
}

/** m:ss wall label for a dialogue timestamp (seconds). */
export function transcriptTime(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  return `${String(Math.floor(total / 60))}:${String(total % 60).padStart(2, '0')}`;
}

/** One merged row of the full transcript feed. */
export interface FullTranscriptRow {
  readonly key: string;
  /** Segment start in seconds (the row renders through transcriptTime). */
  readonly time: number;
  readonly name: string;
  readonly text: string;
}

/** State of the full transcript feed: idle = the tab has not opened yet. */
export type FullTranscriptState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'empty' }
  | {
      readonly kind: 'ready';
      readonly rows: readonly FullTranscriptRow[];
      /** Names of the channels whose transcript failed to fetch or parse (muted notes). */
      readonly failed: readonly string[];
    };

/** The recording's transcript rows in file order (id ascending) - the channel order. */
function transcriptFiles(recording: RoomRecording): readonly RecordingFile[] {
  return recording.files
    .filter((file) => file.purpose === 'transcript')
    .sort((a, b) => a.id - b.id);
}

/**
 * Loads and merges the full transcript feed for one recording. `enabled` opens the feed (the
 * tab is active); the result is cached for the component's lifetime - the parent keys the
 * watch window per recording, so switching recordings remounts and refetches.
 */
export function useFullTranscript(props: {
  readonly recording: RoomRecording;
  readonly token: string;
  readonly fetchImpl: typeof fetch;
  readonly enabled: boolean;
}): FullTranscriptState {
  const { recording, token, fetchImpl, enabled } = props;
  const { t } = useTranslation();
  const [state, setState] = useState<FullTranscriptState>({ kind: 'idle' });
  const cacheRef = useRef<FullTranscriptState | null>(null);

  const files = transcriptFiles(recording);
  const filesKey = files.map((file) => `${String(file.id)}:${file.status}`).join(',');

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (cacheRef.current !== null) {
      setState(cacheRef.current);
      return;
    }
    const ready = files.filter((file) => file.status === 'ready');
    if (ready.length === 0) {
      const empty: FullTranscriptState = { kind: 'empty' };
      cacheRef.current = empty;
      setState(empty);
      return;
    }
    let alive = true;
    setState({ kind: 'loading' });

    // One by-ids call for all channel identities; any failure silently degrades the
    // naming to the «Channel N» fallback (the feed itself must still render). Screen
    // identities (`kc-<uuid>-screen`) are OUR filename convention - Keycloak only knows the
    // base id, so the suffix is stripped before the lookup and reapplied at display time.
    const baseIdentity = (identity: string): string =>
      identity.endsWith('-screen') ? identity.slice(0, -'-screen'.length) : identity;
    const identities = ready.flatMap((file) =>
      file.identity !== undefined ? [baseIdentity(file.identity)] : [],
    );
    const names: Promise<Map<string, DirectoryUser>> = identities
      .length === 0
      ? Promise.resolve(new Map())
      : fetchUsersByIds(token, identities, fetchImpl).then(
          (users) => new Map(users.map((user) => [user.id, user])),
        );
    const namesOrEmpty: Promise<Map<string, DirectoryUser>> = names.catch(() => new Map());

    const channels: Promise<{ readonly doc: TranscriptDoc | null }>[] = ready.map((file) =>
      fetchRecordingFile(recording.roomSlug, recording.id, file.id, token, fetchImpl)
        .then(async (blob) => ({ doc: parseTranscript(JSON.parse(await blob.text()) as unknown) }))
        .catch(() => ({ doc: null })),
    );

    void Promise.all([namesOrEmpty, ...channels]).then((results) => {
      if (!alive) {
        return;
      }
      const userMap = results[0];
      const loaded = results.slice(1) as readonly { readonly doc: TranscriptDoc | null }[];

      // Channel numbering: distinct identities in file order, 1-based; rows without an
      // identity each count as their own channel at their file position.
      const numbers = new Map<string, number>();
      const channelNumber = (file: RecordingFile): number => {
        const key = file.identity ?? `file:${String(file.id)}`;
        let n = numbers.get(key);
        if (n === undefined) {
          n = numbers.size + 1;
          numbers.set(key, n);
        }
        return n;
      };
      const channelName = (file: RecordingFile): string => {
        const fallback = t('recordings.full_channel_fallback', {
          n: channelNumber(file),
        });
        const user = file.identity !== undefined ? userMap.get(baseIdentity(file.identity)) : undefined;
        const base =
          user !== undefined && user.fullName.length > 0
            ? user.fullName
            : user !== undefined && user.username.length > 0
              ? user.username
              : fallback;
        return file.identity !== undefined && file.identity.endsWith('-screen')
          ? `${t('recordings.full_screen_prefix')}: ${base}`
          : base;
      };

      const rows: FullTranscriptRow[] = [];
      const failed: string[] = [];
      ready.forEach((file, index) => {
        const result = loaded[index];
        if (result === undefined || result.doc === null || result.doc.segments.length === 0) {
          failed.push(channelName(file));
          return;
        }
        const name = channelName(file);
        result.doc.segments.forEach((segment, segIndex) => {
          rows.push({
            key: `${String(file.id)}:${String(segIndex)}`,
            time: segment.start,
            name,
            text: segment.text,
          });
        });
      });
      // Array#sort is stable: equal starts keep the channel (file) order as the tie-break.
      rows.sort((a, b) => a.time - b.time);
      const next: FullTranscriptState = { kind: 'ready', rows, failed };
      cacheRef.current = next;
      setState(next);
    });

    return () => {
      alive = false;
    };
  }, [enabled, filesKey, recording.roomSlug, recording.id, token, fetchImpl, t]);

  return state;
}

/** The «Полная транскрибация» view: loading, empty, or the merged chronological feed. */
export function FullTranscriptView(props: {
  readonly state: FullTranscriptState;
}): JSX.Element {
  const { t } = useTranslation();
  if (props.state.kind === 'idle' || props.state.kind === 'loading') {
    return (
      <div
        data-testid="recordings-full-loading"
        className="flex min-h-[280px] items-center justify-center p-6"
      >
        <LoaderCircle aria-hidden className="size-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-[13px] text-muted-foreground">
          {t('recordings.full_loading')}
        </span>
      </div>
    );
  }
  if (props.state.kind === 'empty') {
    return (
      <div
        data-testid="recordings-full-empty"
        className="flex min-h-[280px] items-center justify-center p-6 text-center text-[13px] text-muted-foreground"
      >
        {t('recordings.full_empty')}
      </div>
    );
  }
  return (
    <div data-testid="recordings-full-transcript" className="scroll-thin max-h-[62vh] overflow-y-auto">
      {props.state.rows.length > 0 ? (
        <ul className="divide-y divide-border">
          {props.state.rows.map((row) => (
            <li key={row.key} className="flex gap-3 px-4 py-2.5">
              <span className="shrink-0 pt-0.5 font-mono text-[11.5px] tabular-nums text-muted-foreground">
                {transcriptTime(row.time)}
              </span>
              <span className="text-[13px] leading-relaxed text-foreground">
                <span className="font-medium">{row.name}</span>
                {': '}
                {row.text}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {props.state.failed.map((name, index) => (
        <p
          key={`failed:${String(index)}`}
          data-testid="recordings-full-channel-failed"
          className="px-4 py-2 text-[12px] text-muted-foreground"
        >
          {t('recordings.full_channel_failed', { name })}
        </p>
      ))}
    </div>
  );
}
